import Phaser from 'phaser';
import { ENEMIES } from '../data/enemies';
import { TOWER_ART } from '../data/towerVisuals';
import { VOICE_LINES } from '../data/voiceManifest';
import { MAP_PROP_TEXTURES } from '../data/mapProps';

/**
 * Loads the large tactical art and voice pack only when the player leaves the
 * landing menu. The Phaser texture/audio caches are global, so later scenes
 * can use the assets exactly as they did when Boot loaded them all up front.
 */
export class RuntimeAssetsScene extends Phaser.Scene {
  static ready = false;
  static loading = false;
  private static waiters: Array<() => void> = [];

  constructor() {
    super('RuntimeAssets');
  }

  /** Begin filling Phaser's shared caches without blocking the current scene. */
  static startInBackground(scene: Phaser.Scene): void {
    if (RuntimeAssetsScene.ready || RuntimeAssetsScene.loading) return;
    RuntimeAssetsScene.loading = true;
    scene.scene.launch('RuntimeAssets');
  }

  static ensureStarted(scene: Phaser.Scene): Promise<void> {
    if (RuntimeAssetsScene.ready) return Promise.resolve();
    RuntimeAssetsScene.startInBackground(scene);
    return new Promise((resolve) => RuntimeAssetsScene.waiters.push(resolve));
  }

  preload(): void {
    this.load.setBaseURL('');

    this.load.image('tower-ballistic', 'assets/art/tower-ballistic.png');
    this.load.image('tower-thermal', 'assets/art/tower-thermal.png');
    this.load.image('tower-artillery', 'assets/art/tower-artillery.png');
    this.load.image('tower-energy', 'assets/art/tower-energy.png');
    this.load.image('tower-vulcan', 'assets/art/tower-vulcan.png');
    this.load.image('tower-sabre', 'assets/art/tower-sabre.png');
    this.load.image('tower-tesla', 'assets/art/tower-tesla.png');
    this.load.image('tower-cryo', 'assets/art/tower-cryo.png');
    this.load.image('tower-dronehive', 'assets/art/ability-drone.png');
    this.load.image('tower-dronehive-v2', 'assets/art/tower-dronehive-v2.png');
    this.load.image('top-dronehive-v2', 'assets/art/top-dronehive-v2.png');
    this.load.image('swarm-drone', 'assets/art/ability-drone.png');
    this.load.image('enemy-scuttler', 'assets/art/enemy-scuttler.png');
    this.load.image('enemy-plateback', 'assets/art/enemy-plateback.png');
    this.load.image('enemy-colossus', 'assets/art/enemy-colossus.png');
    this.load.image('top-ballistic', 'assets/art/top-ballistic.png');
    this.load.image('top-thermal', 'assets/art/top-thermal.png');
    this.load.image('top-artillery', 'assets/art/top-artillery.png');
    this.load.image('top-energy', 'assets/art/top-energy.png');
    this.load.image('top-vulcan', 'assets/art/top-vulcan.png');
    this.load.image('top-sabre', 'assets/art/top-sabre.png');
    this.load.image('top-tesla', 'assets/art/top-tesla.png');
    this.load.image('top-cryo', 'assets/art/top-cryo.png');
    this.load.image('top-scuttler', 'assets/art/top-scuttler.png');
    this.load.image('top-plateback', 'assets/art/top-plateback.png');
    this.load.image('top-colossus', 'assets/art/top-colossus.png');
    this.load.image('unbeaten-door-clean', 'assets/art/unbeaten-door-clean.png');
    this.load.image('unbeaten-door-graffiti', 'assets/art/unbeaten-door-graffiti.png');
    this.load.image('command-bastion-v1', 'assets/art/command-bastion-v1.png');
    this.load.image('enemy-archon-v2', 'assets/art/enemy-archon-v2.png');
    this.load.image('enemy-hivequeen-v2', 'assets/art/enemy-hivequeen-v2.png');
    this.load.image('enemy-reaperbot-v2', 'assets/art/enemy-reaperbot-v2.png');
    this.load.image('enemy-scuttler-v2', 'assets/art/enemy-scuttler-v2.png');
    this.load.image('enemy-husk-v2', 'assets/art/enemy-husk-v2.png');
    this.load.image('enemy-dashling-v2', 'assets/art/enemy-dashling-v2.png');
    this.load.image('enemy-plateback-v2', 'assets/art/enemy-plateback-v2.png');
    this.load.image('enemy-gunner-v2', 'assets/art/enemy-gunner-v2.png');
    this.load.image('enemy-goliath-v2', 'assets/art/enemy-goliath-v2.png');
    this.load.image('enemy-nulltick-v2', 'assets/art/enemy-nulltick-v2.png');
    this.load.image('enemy-venomspitter-v2', 'assets/art/enemy-venomspitter-v2.png');
    this.load.image('enemy-locust-v2', 'assets/art/enemy-locust-v2.png');
    this.load.image('top-vulcan-v2', 'assets/art/top-vulcan-v2.png');

    ['ballistic', 'thermal', 'artillery', 'energy', 'vulcan', 'sabre', 'tesla', 'cryo', 'dronehive'].forEach((id) => {
      this.load.image(`top-${id}-v3`, `assets/art/top-${id}-v3.png`);
      this.load.image(`tower-${id}-v3`, `assets/art/tower-${id}-v3.png`);
      this.load.image(`tower-${id}-v4`, `assets/art/tower-${id}-v4.png`);
    });
    for (const spec of Object.values(TOWER_ART)) {
      this.load.image(spec.bodyKey, `assets/art/${spec.bodyKey}.png`);
    }

    this.load.image('battle-kappa-bg', 'assets/art/battle-kappa-bg.png');
    this.load.image('battle-helios-bg', 'assets/art/battle-helios-bg.png');
    this.load.image('battle-cinder-bg', 'assets/art/battle-cinder-bg.png');
    this.load.image('battle-meridian-bg', 'assets/art/battle-meridian-bg.png');
    this.load.image('battle-ashfall-bg', 'assets/art/battle-ashfall-bg.png');
    this.load.image('battle-theta-bg', 'assets/art/battle-theta-bg.png');
    this.load.image('battle-obsidian-bg', 'assets/art/battle-obsidian-bg.png');
    this.load.image('battle-cryostation-bg', 'assets/art/battle-cryostation-bg.png');
    this.load.image('battle-floodway-bg', 'assets/art/battle-meridian-floodway-bg.png');
    this.load.image('battle-verdigris-bg', 'assets/art/battle-verdigris-bg.png');
    this.load.image('battle-umbra-bg', 'assets/art/battle-umbra-bg.png');
    this.load.image('battle-aurora-bg', 'assets/art/battle-aurora-bg.png');
    this.load.image('battle-emberfall-bg', 'assets/art/battle-emberfall-bg.png');
    this.load.image('battle-tideline-bg', 'assets/art/battle-tideline-bg.png');
    this.load.image('icon-kestrel', 'assets/art/icon-kestrel.png');
    this.load.image('icon-pyre', 'assets/art/icon-pyre.png');
    this.load.image('icon-mordant', 'assets/art/icon-mordant.png');
    this.load.image('icon-helios', 'assets/art/icon-helios.png');
    this.load.image('icon-vulcan', 'assets/art/icon-vulcan.png');
    this.load.image('icon-sabre', 'assets/art/icon-sabre.png');
    this.load.image('icon-tesla', 'assets/art/icon-tesla.png');
    this.load.image('icon-cryo', 'assets/art/icon-cryo.png');
    this.load.image('ability-spear-v2', 'assets/art/ability-spear-v2.png');
    this.load.image('ability-barrier-v2', 'assets/art/ability-barrier-v2.png');
    this.load.image('ability-drone', 'assets/art/ability-drone.png');
    this.load.image('rolling-death-bomb-v1', 'assets/art/rolling-death-bomb-v1.png');
    this.load.image('rolling-death-balls-v1', 'assets/art/rolling-death-balls-v1.png');
    this.load.image('rolling-death-snow-v1', 'assets/art/rolling-death-snow-v1.png');
    this.load.image('rolling-death-cooldown-v1', 'assets/art/rolling-death-cooldown-v1.png');
    this.load.image('symbiosis-addon-icons-v1', 'assets/art/symbiosis-addon-icons-v1.png');
    this.load.image('symbiosis-pod-missile-v1', 'assets/art/symbiosis-pod-missile-v1.png');
    this.load.image('symbiosis-pod-autocannon-v1', 'assets/art/symbiosis-pod-autocannon-v1.png');
    this.load.image('symbiosis-pod-arc-v1', 'assets/art/symbiosis-pod-arc-v1.png');
    this.load.image('symbiosis-pod-shield-v1', 'assets/art/symbiosis-pod-shield-v1.png');
    this.load.image('symbiosis-addon-icons-v2', 'assets/art/symbiosis-addon-icons-v2.png');
    this.load.image('symbiosis-pod-laser-v1', 'assets/art/symbiosis-pod-laser-v1.png');
    this.load.image('symbiosis-pod-frost-v1', 'assets/art/symbiosis-pod-frost-v1.png');
    this.load.image('symbiosis-pod-plasma-v1', 'assets/art/symbiosis-pod-plasma-v1.png');
    this.load.image('symbiosis-pod-cluster-v1', 'assets/art/symbiosis-pod-cluster-v1.png');
    this.load.image('ability-emp-v2', 'assets/art/ability-emp-v2.png');
    this.load.image('ui-command-grid', 'assets/art/ui-command-grid.png');
    this.load.image('command-emblem', 'assets/art/command-emblem.png');
    this.load.image('award-crest-v1', 'assets/art/award-crest-v1.png');
    for (const key of [
      'firstblood', 'mission', 'perfect', 'kills', 'explosive', 'thermal',
      'energy', 'ballistic', 'waves', 'boss', 'ability', 'rank',
    ] as const) {
      this.load.image(`award-medal-${key}-v1`, `assets/art/award-medal-${key}-v1.png`);
    }
    this.load.image('command-bastion-v2', 'assets/art/command-bastion-v2.png');
    this.load.image('map-portal-warp-v1', 'assets/art/world/map-portal-warp-v1.png');
    this.load.spritesheet('map-prop-environmental-life', 'assets/art/world/map-environmental-life-v1.png', { frameWidth: 512, frameHeight: 512 });
    this.load.image(MAP_PROP_TEXTURES['wrecked-tank'], 'assets/art/props/wrecked-tank-v1.png');
    this.load.image(MAP_PROP_TEXTURES['aircraft-wreck'], 'assets/art/props/aircraft-wreck-v1.png');
    this.load.image(MAP_PROP_TEXTURES.barrels, 'assets/art/props/barrels-v1.png');
    this.load.image(MAP_PROP_TEXTURES.crates, 'assets/art/props/crates-v1.png');
    this.load.image(MAP_PROP_TEXTURES.debris, 'assets/art/props/debris-v1.png');
    this.load.image(MAP_PROP_TEXTURES['turret-wreck'], 'assets/art/top-ballistic-v3.png');
    this.load.image(MAP_PROP_TEXTURES['weapon-cache'], 'assets/art/tower-artillery-v4.png');
    this.load.image(MAP_PROP_TEXTURES['sensor-array'], 'assets/art/ability-drone.png');
    this.load.image(MAP_PROP_TEXTURES['symbiosis-pod'], 'assets/art/symbiosis-pod-arc-v1.png');
    this.load.image(MAP_PROP_TEXTURES['command-crate'], 'assets/art/command-emblem.png');

    for (const line of VOICE_LINES) {
      this.load.audio(`voice-${line.id}`, `assets/voice/${line.file}`);
    }
    for (const id of Object.keys(ENEMIES)) {
      this.load.image(`enemy-${id}`, `assets/art/enemy-${id}.png`);
    }
    for (const id of Object.keys(ENEMIES)) {
      const version = id === 'siegebreaker' ? 'v3' : 'v2';
      this.load.image(`enemy-${id}-${version}`, `/assets/art/enemy-${id}-${version}.png`);
    }
  }

  create(): void {
    RuntimeAssetsScene.ready = true;
    RuntimeAssetsScene.loading = false;
    const waiters = RuntimeAssetsScene.waiters.splice(0);
    waiters.forEach((resolve) => resolve());
    this.scene.stop();
  }
}
